import React, { PropsWithChildren } from 'react';
import classNames from 'classnames';
import { Icon } from '../icons/Icon';
import styles from './Stepper.module.scss';

/**
 * Stepper component which allows for more in-depth customization per step. Also requires
 * step phases to be handled on the call-side.
 * @example ```
 * <Stepper>
 *     <Stepper.Step phase={stepper.determinePhase(0, currentStep)} onClick={() => setCurrentStep(0)}>
 *         <Icon type='general-ui-brush' />
 *     </Stepper.Step>
 *     <Stepper.Step phase={stepper.determinePhase(1, currentStep)} onClick={() => setCurrentStep(1)}>
 *         <Icon type='general-ui-crop' />
 *     </Stepper.Step>
 *     <Stepper.Step phase={stepper.determinePhase(2, currentStep)} onClick={() => setCurrentStep(2)}>
 *         <Icon type='shopping-cart' />
 *     </Stepper.Step>
 * </Stepper>
 * ```
 */
export const Stepper = ({ hide, children, className }: Props) => {

    const classes = classNames(styles.container, className, {
        [styles.hide]: hide
    });

    return (
        <div className={classes}>
            {children}
        </div>
    );
};

/**
 * @see Stepper
 */
Stepper.Step = ({ phase, children, disabled, onClick }: StepperStepProps) => {

    const className = classNames(styles.step, styles[phase], {
        [styles.disabled]: disabled || !onClick
    });

    return (
        <div className={className} onClick={onClick}>
            <div className={styles.markOuter}>
                <div className={styles.mark}>
                    <div className={styles.markInner} />
                    <Icon className={styles.icon} type='general-ui-checkmark' />
                </div>
            </div>

            <div className={styles.stepchildren}>
                {children}
            </div>
        </div>
    );
};

Stepper.determinePhase = (index: number, currentIndex: number) => {
    if (index < currentIndex) {
        return 'before';
    } else if (index === currentIndex) {
        return 'active';
    } else {
        return 'after';
    }
};

type StepperStepProps = PropsWithChildren<{
    phase: 'before' | 'active' | 'after';
    disabled?: boolean;
    onClick?: () => void;
}>;

type Props = PropsWithChildren<{
    className?: string;
    hide?: boolean;
}>;